import LocalStorageKey from '@enums/LocalStorageKey';

class LocalStorageService {
  private prefix: string;

  constructor(prefix: string) {
    this.prefix = prefix;
  }

  private getPrefixedKey(key: LocalStorageKey): string {
    return `${this.prefix}_${key}`;
  }

  setItem<T>(key: LocalStorageKey, value: T): void {
    const prefixedKey = this.getPrefixedKey(key);
    if (value === undefined) {
      // JSON.stringify(undefined) is undefined; localStorage would coerce
      // that to the literal string "undefined", which then reads back as a
      // truthy sentinel that trips the WORKSPACE mismatch reload in
      // AuthContext — reloading the user onto /auth/signup?step=7 exactly
      // when they clicked Continue on Platforms. Treat as a clear.
      localStorage.removeItem(prefixedKey);
      return;
    }
    const serializedValue = JSON.stringify(value);
    localStorage.setItem(prefixedKey, serializedValue);
  }

  getItem<T>(key: LocalStorageKey): T | null {
    const prefixedKey = this.getPrefixedKey(key);
    const serializedValue = localStorage.getItem(prefixedKey);
    // Legacy: previously-buggy writes stored the literal string "undefined".
    if (!serializedValue || serializedValue === 'undefined') return null;
    try {
      return JSON.parse(serializedValue) as T;
    } catch (_err) {
      // Fallback for legacy/plain values that were not JSON-encoded
      return serializedValue as unknown as T;
    }
  }

  removeItem(key: LocalStorageKey): void {
    const prefixedKey = this.getPrefixedKey(key);
    localStorage.removeItem(prefixedKey);
  }

  clearWorkspace(workspaceName: string): void {
    // Clear all keys with the workspace name prefix in the name
    Object.keys(localStorage).forEach((key) => {
      if (key.includes(workspaceName)) {
        localStorage.removeItem(key);
      }
    });
  }

  clear(): void {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(this.prefix)) {
        localStorage.removeItem(key);
      }
    });
  }
}

export default new LocalStorageService(LocalStorageKey.APP_STORAGE_PREFIX);
